import {CellSubType, CellType, ColumnAggregationType} from "utils/enums";
import { TimeSpan, convertTimeSpanToString, timeSpanToSeconds } from "utils/time-utils";
import {HighlightColor, HighlightRange, Nullable, IColumnsWithValuesProps, ITableDataEntryValue, IProductTemplate, ITableDataEntry, Filter} from "./interfaces";
import { IForeignKeyValue, MultiForeignKeyValue } from "../components/templates/foreign-key/ForeignKey";
import { IUserSignature, MultiValue } from "./cellInterfaces";

const isInRange = (value: number | null | undefined, range: HighlightRange) => {
    if (value == null) return false;

    const rMin = range.min?.value ?? Number.MIN_SAFE_INTEGER;
    const rMax = range.max?.value ?? Number.MAX_SAFE_INTEGER
    const minInclusive = range.min?.inclusive ?? false
    const maxInclusive = range.max?.inclusive ?? false


    if (rMin > value || (!minInclusive && rMin === value)) return false

    return !(rMax < value || (!maxInclusive && rMax === value));
}

const getValueColors = (value: number|undefined, {
    defaultHighlightColor,
    highlightRanges
}: { highlightRanges?: Nullable<Array<HighlightRange>>, defaultHighlightColor?: Nullable<HighlightColor> }) => {
    let bgColor;
    let color;
    let newValue = value;
    
    if (typeof value === "object") {
      const seconds = timeSpanToSeconds(value);
      newValue = seconds === 0 ? undefined : seconds;
    }
    
    if (highlightRanges && typeof newValue === "number") {
      
      highlightRanges.forEach(range => {
        if (isInRange(newValue, range)) {
          range.color && (color = range.color);
          range.bgColor && (bgColor = range.bgColor);
        }
      });
    }

    if (defaultHighlightColor && !bgColor && !color) {
        defaultHighlightColor.color && (color = defaultHighlightColor.color);
        defaultHighlightColor.bgColor && (bgColor = defaultHighlightColor.bgColor);
    }

    return [color, bgColor];
}

const getHighlightColor = (highlightColor?: Nullable<HighlightColor>) => {
    const bgColor = highlightColor?.bgColor;
    const color = highlightColor?.color;

    return [color, bgColor];
}

const checkForUnwantedType = (type: string | undefined) => {
    return !(type === "USER_SIGNATURE" || type === "PARAMETERS" || type === "FOREIGN_KEY")
}

const checkForHighlightType = (type: string | undefined) => {
    return type === "DOUBLE" || type === "LONG" || type === "TIMESPAN"
}

const getDataStringParamsToObject = (dateFrom?: string, dateTo?: string, filter?: string) => {
    if (dateFrom || dateTo || filter) {
        const tempSearchParams: { dateFrom?: string, dateTo?: string, filter?: string } = {};
        if (dateFrom) {
            tempSearchParams.dateFrom = dateFrom;
        }
        if (dateTo) {
            tempSearchParams.dateTo = dateTo;
        }
        if (filter) {
            tempSearchParams.filter = filter;
        }
        return {params: tempSearchParams}
    }
    return undefined
}

const filterPreviewValidation = (dateFrom?: string, dateTo?: string, filter?: string): { params: any } | undefined => {
    if (!dateFrom && !dateTo && !filter) {
        return undefined
    }
    if(dateFrom && !dateTo && !filter){
        return undefined
    }
    if(!dateFrom && dateTo && !filter){
        return undefined
    }

    const tempSearchParams: { dateFrom?: string, dateTo?: string, filter?: string } = {};
    if (dateFrom) {
        tempSearchParams.dateFrom = dateFrom;
    }
    if (dateTo) {
        tempSearchParams.dateTo = dateTo;
    }
    if (filter) {
        tempSearchParams.filter = filter;
    }

    return {params: tempSearchParams}
}

const getValueFromTableDataType = (cell: ITableDataEntryValue | undefined) => {
  if (!cell) return "-";
  if (cell.value !== 0 && !cell.value) return "-";
  if (
    cell.type === CellType.STRING ||
    cell.type === CellType.LONG ||
    cell.type === CellType.DOUBLE ||
    cell.type === CellType.DATETIME ||
    cell.type === CellType.TIME ||
    cell.type === CellType.INSTANT
  ) {
    return cell.value;
  }
  if (cell.type === CellType.USER_SIGNATURE) {
    const val = cell.value as IUserSignature;
    if (val.signed) return val.display;
    return "Unsigned";
  }
  if (cell.type === CellType.TIMESPAN) {
    return convertTimeSpanToString(cell.value as TimeSpan);
  }
  if (cell.type === CellType.FOREIGN_KEY) {
    const val = cell.value as IForeignKeyValue;
    if (val.represantationString) return val.represantationString;
    return "-";
  }
  if (cell.type === CellType.MULTI_VALUE) {
    const multiCell = cell.value as MultiValue
    if (multiCell.values.length === 0) return "-";
    if (multiCell.subType === CellSubType.TIMESPAN) {
      return multiCell.values.map(v => convertTimeSpanToString(v as TimeSpan)).join(" | ");
    }
    return multiCell.values.join(" | ");
  }
  if (cell.type === CellType.MULTI_FOREIGN_KEY) {
    const multiForeignCell = cell.value as MultiForeignKeyValue;
    if (multiForeignCell.objectIds.length === 0) return "-";
    return multiForeignCell.represantationString;
  }
  return `Type not implemented`;
}

const arrayToCsv = (arr: Array<any>, transposed: boolean = false) => {
    if (!arr) {
        return '';
    }
    let csv = "";
    if (transposed) {
      for (let i = 0; i < arr[0].length; i++) {
        csv += `${arr[0][i].name},${arr.map((col: any) => col.find((_: any, index: number) => i === index).value).join(",")}\n`;
      }
      return csv;
    }
    
    csv += `${arr[0].map((item: any) => item.name).join(",")}\n`;
    for (let i = 0; i < arr.length; i++) {
        csv += `${arr[i].map((item: any) => `"${item.value}"`).join(",")}\n`;
    }
    return csv;
}

const arrayToCsvfromCharts = (arr: Array<any>) => {
  if (!arr || arr.length === 0) {
    return "";
  }

  const keys = Object.keys(arr[0]);
  const csvRows = [keys.join(",")];

  arr.forEach(row => {
    const values = keys.map(key => `"${row[key]}"`);
    csvRows.push(values.join(","));
  });

  return csvRows.join("\n");
};

const downloadBlob = (content: any, filename: string, contentType: string) => {
    const blob = new Blob([content], {type: contentType});
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}

const s2ab = (s: any) => {
    const buf = new ArrayBuffer(s?.length);
    const view = new Uint8Array(buf);

    for (let i = 0; i < s?.length; i++) {
        view[i] = s.charCodeAt(i) & 0xff;
    }

    return buf;
}

const columnsTotalsAggregation = (columns: IColumnsWithValuesProps[]) => {
    return columns.map((col) => {
            if (col.aggregationType === ColumnAggregationType.SUM) {
                col.total = col.values.reduce((a: number, b: number) => a + b, 0);
            }

            if (col.aggregationType === ColumnAggregationType.MEAN) {
                col.total = (col.values.reduce((a: number, b: number) => a + b, 0) / col.values.length);
            }

            if (col.aggregationType === ColumnAggregationType.COUNT) {
                col.total = col.values.length;
            }
            return col;
        }
    );
};

const formatDataTotalsAggregation = (
  template: IProductTemplate | undefined,
  data: ITableDataEntry[] | undefined
) => {
  const tempColumnsWithValues: IColumnsWithValuesProps[] = [];
  template?.data?.attributes.forEach(col => {
    const temp: IColumnsWithValuesProps = { referenceName: "", values: [] };
    temp.referenceName = col.referenceName;
    temp.aggregationType = col.aggregationType;
    temp.decimalPlaces = col.decimalPlaces;
    temp.unit = col.unit;
    temp.values = [];
    data?.forEach(entry => {
      if (
        entry.entries[col.referenceName]?.value ||
        entry.entries[col.referenceName]?.value === 0
      ) {
        temp.values.push(entry.entries[col.referenceName].value);
      }
    });
    tempColumnsWithValues.push(temp);
  });

  return columnsTotalsAggregation(tempColumnsWithValues);
};

const handlePrecision = (number: number, precision: number, output: boolean = false) => {
    try {
        if (output) {
            return new Intl.NumberFormat('lt-LT', {
                minimumFractionDigits: precision,
                maximumFractionDigits: precision
            })
                .format(number)
        } 
            return number.toFixed(precision);
        
    } catch (err) {
        return number;
    }
}

const formatDataPatternToCalendar = (datePattern: string) => {
  return datePattern && typeof datePattern === "string"
    ? datePattern.replace(/yyyy/g, "yy").replace(/MM/g, "mm")
    : "";
};

const extractFilters = (condition: string) => {
  const parts = condition.split(/[^\w\s]+/g).filter(p => p); 

  let obj: Filter = { reference: '', validation: '', value: '' }; 
  const rules = parts.reduce((array, val, i) => {
    if (i % 3 === 0) {
      obj.reference = val;
    }
    if (i % 3 === 1) {
      obj.validation = val;
    }
    if (i % 3 === 2) {
      obj.value = val;
      array.push(obj);
      obj = { reference: '', validation: '', value: '' };
    }
    return array
  }, [] as Filter[])

  return rules;
}

export {
  isInRange,
  getValueColors,
  getHighlightColor,
  checkForUnwantedType,
  checkForHighlightType,
  getDataStringParamsToObject,
  filterPreviewValidation,
  getValueFromTableDataType,
  arrayToCsv,
  arrayToCsvfromCharts,
  downloadBlob,
  s2ab,
  columnsTotalsAggregation,
  formatDataTotalsAggregation,
  handlePrecision,
  formatDataPatternToCalendar,
  extractFilters,
};